import { useState, useMemo, useEffect } from 'react';
import { Typography, RichTextViewer, TextButton } from 'seagull';
import { getCoverCCFeesChangeBullet, getCoverCCFeesText, getNetTermsChangeBullet } from "../../../business-logic/amendmentsLogic";
import RichTextViewerModal from "../../../components/RichTextViewerModal";
import { useOverlayContext, useIoContext, useStore, useStoreActions } from "../../../contexts";
import { PracticeManagementSyncFailures } from "../../../integrations/PracticeManagement/PracticeManagementSyncFailures/PracticeManagementSyncFailures";
import { legalTermsModel, relationshipModel } from "../../../models";
import { AgreementDataModel } from "../../../models/AgreementDataModel";
import { selectors } from "../../../store/state-selectors";
import styles from "./AgreementView.module.scss";
import AgreementAmendmentsApprovalMethod from "./components/AgreementAmendmentsApprovalMethod";
import AgreementInvoicesApprovalMethod from "./components/AgreementInvoicesApprovalMethod";
import AmendmentChangesInAgreementView from "./components/AmendmentChangesInAgreementView";
import ChangesSummary from "./components/ChangesSummary";
import { AgreementViewCard, SpecificTerm } from "./components/CommonComponents";
import Header from "./components/Header";
import SignedHeader from "./components/SignedHeader";
import StatementOfWork from "./components/StatementOfWork";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const autoprefix = 'agreement-view-';
const pdfViewUrlLoader = 'pdfViewUrl';
export default function AgreementView(props) {
  var _relationship$practic;
  const {
    relationship,
    isVendor,
    refresh
  } = props;
  const overlayContext = useOverlayContext();
  const {
    openNewTab
  } = useIoContext();
  const {
    extraClause
  } = relationship;
  const {
    buttonPrimary
  } = useStore(selectors.user.branding);
  const qboClasses = useStore(state => state.qboClasses.data);
  const {
    api,
    fetchQboClasses
  } = useStoreActions();
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [legalTermsUrl, setLegalTermsUrl] = useState();
  const updatedNetTerms = relationshipModel.updatedNetTerms(relationship);
  const netTermsChangeBullet = updatedNetTerms ? getNetTermsChangeBullet(relationship, updatedNetTerms.value()) : null;
  const updatedIsVendorCoveringCCFee = relationshipModel.updatedIsVendorCoveringCCFee(relationship);
  const agreementData = AgreementDataModel.fromRelationship(relationship);
  useEffect(() => {
    fetchQboClasses();
  }, []);
  const coverCCFeesChangeBullet = updatedIsVendorCoveringCCFee ? getCoverCCFeesChangeBullet(relationship, updatedIsVendorCoveringCCFee.value()) : null;
  const richText = useMemo(() => {
    return legalTermsModel.richTextContent(relationship.legalTerms);
  }, [relationship]);
  const effectiveDateTerm = relationship.effectiveDate && /*#__PURE__*/_jsx(SpecificTerm, {
    field: "Effective date of agreement",
    value: relationshipModel.formatEffectiveDate(relationship),
    testid: autoprefix + 'effective-date'
  });
  async function onViewLegalTerms() {
    if (legalTermsModel.isPDF(relationship.legalTerms)) {
      const url = await getLegalTermsUrl(relationship.legalTerms);
      openNewTab(url);
    } else {
      setShowViewerModal(true);
    }
  }
  async function getLegalTermsUrl(legalTerms) {
    if (legalTermsUrl) {
      return legalTermsUrl;
    }
    overlayContext.startLoader(pdfViewUrlLoader);
    const pdfViewUrl = await api.business.getLegalTermsDownloadURL(legalTerms.pdfUniqueName, relationship.vendor.id).finally(() => {
      overlayContext.stopLoader(pdfViewUrlLoader);
    });
    if (!pdfViewUrl) {
      throw new Error('legalTermsUrlError');
    }
    setLegalTermsUrl(pdfViewUrl);
    return pdfViewUrl;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Header, {
      relationship: relationship,
      onUpdate: refresh
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.main,
      "data-testid": "agreement-view",
      children: [/*#__PURE__*/_jsx(ChangesSummary, {
        isVendor: isVendor,
        relationship: relationship,
        testid: autoprefix + 'changes',
        refresh: refresh
      }), ((_relationship$practic = relationship.practiceManagementSyncFailures) === null || _relationship$practic === void 0 ? void 0 : _relationship$practic.length) && /*#__PURE__*/_jsx(PracticeManagementSyncFailures, {
        relationship: relationship,
        refresh: refresh
      }), /*#__PURE__*/_jsx(SignedHeader, {
        relationship: relationship,
        brandColor: buttonPrimary,
        testPrefix: autoprefix
      }), /*#__PURE__*/_jsx(AgreementViewCard, {
        headTitle: "Services Provided",
        children: /*#__PURE__*/_jsx("div", {
          className: styles.services,
          children: /*#__PURE__*/_jsx(StatementOfWork, {
            services: relationship.services,
            isVendor: isVendor,
            qboClasses: qboClasses,
            serviceDetailsProps: {
              agreementData,
              isVendor
            }
          })
        })
      }), /*#__PURE__*/_jsx(AgreementViewCard, {
        headTitle: "Agreement terms",
        children: /*#__PURE__*/_jsxs("div", {
          className: styles.specificTerms,
          children: [effectiveDateTerm, /*#__PURE__*/_jsx(SpecificTerm, {
            field: "Payment terms",
            value: relationshipModel.paymentTermsText(relationship),
            testid: autoprefix + 'net-terms',
            changes: netTermsChangeBullet && /*#__PURE__*/_jsx(AmendmentChangesInAgreementView, {
              disableBorderRadius: true,
              items: [netTermsChangeBullet],
              autoApprovalDate: relationshipModel.updatedNetTermsAutoApprovalDate(relationship),
              testid: autoprefix + 'net-terms-changes'
            })
          }), /*#__PURE__*/_jsx(SpecificTerm, {
            testid: autoprefix + 'cc-fees',
            field: "Credit card fees",
            value: getCoverCCFeesText(relationshipModel.actualIsVendorCoveringCCFee(relationship), relationshipModel.vendorName(relationship), relationshipModel.customerName(relationship)),
            changes: coverCCFeesChangeBullet && /*#__PURE__*/_jsx(AmendmentChangesInAgreementView, {
              disableBorderRadius: true,
              items: [coverCCFeesChangeBullet],
              autoApprovalDate: relationshipModel.updatedIsVendorCoveringCCFeeAutoApprovalDate(relationship),
              testid: autoprefix + 'cc-fees-changes'
            })
          }), /*#__PURE__*/_jsx(AgreementAmendmentsApprovalMethod, {
            relationship: relationship,
            testid: autoprefix
          }), /*#__PURE__*/_jsx(AgreementInvoicesApprovalMethod, {
            relationship: relationship,
            testid: autoprefix
          }), extraClause && /*#__PURE__*/_jsx(SpecificTerm, {
            field: "Notes",
            value: /*#__PURE__*/_jsx(RichTextViewer, {
              "data-testid": 'extra-clause-rich-text-viewer',
              value: extraClause,
              skin: "secondary"
            })
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.tnc,
            children: [/*#__PURE__*/_jsx(Typography, {
              weight: "bold",
              children: "Additional terms of service:"
            }), "\xA0", /*#__PURE__*/_jsx(TextButton, {
              "data-testid": autoprefix + "btn-view",
              onClick: onViewLegalTerms,
              children: "Read more"
            })]
          }), showViewerModal && /*#__PURE__*/_jsx(RichTextViewerModal, {
            show: true,
            title: relationship.legalTerms.name,
            text: richText,
            onCancel: () => setShowViewerModal(false)
          })]
        })
      })]
    })]
  });
}